import { Tooltip } from 'antd'
import Table, { SortOrder } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { useDateTime } from '../../../shared/useDateTime'
import { CancellationType, EmployeeDisplayTinyInfo, EmployeeLevelEnum, ResignedEmployeeDto } from '../../../types'
import { getSortOrderByEmployeeLevel } from '../../companySettings/employees/employeesLevelSorterUtil'
import { MeetingHistoryModal } from './MeetingHistoryModal'

type ResignedEmployeesListProps = {
  listData: ResignedEmployeeDto[]
  employeeLevels: EmployeeLevelEnum[]
}

export const ResignedEmployeesList: FC<ResignedEmployeesListProps> = ({ listData, employeeLevels }) => {
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { formatDateShort, formatDateShortWithTimezone } = useDateTime(user)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<undefined | ResignedEmployeeDto>(undefined)
  const [t] = useTranslation()
  const { tableActionButtonsRowWidth } = tableActionButtons()

  const compareMoneyValues = (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto, moneyType: 'targetBonus' | 'salary') => {
    const salaryA = employeeA[moneyType] ? employeeA[moneyType] : 0
    const salaryB = employeeB[moneyType] ? employeeB[moneyType] : 0
    return salaryA - salaryB
  }

  const sortDates = (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto, dateType: 'startDate' | 'endDate') => {
    const A = employeeA[dateType] ? employeeA[dateType] : ''
    const B = employeeB[dateType] ? employeeB[dateType] : ''
    return A.localeCompare(B)
  }

  const cancellationReasonTranslation = (data: ResignedEmployeeDto) =>
    data.reason ? t(`global.employeeCancellationType.${data.reason}`) : t(`global.employeeCancellationType.${CancellationType.Unknown}`)

  const employeeTableHeader = <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('employeeList.table.employee')}</Tooltip>

  const columns = [
    {
      key: 'employee',
      title: employeeTableHeader,
      className: 'minWidth-column-xl textAlign-left',
      sorter: (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto) => {
        const nameA = computeFullname(user.nameDisplayPreference, employeeA.firstName, employeeA.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, employeeB.firstName, employeeB.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (tableData: ResignedEmployeeDto) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={tableData.id}
          firstName={tableData.firstName}
          lastName={tableData.lastName}
          linkToProfile={
            tableData.id !== user.employeeId
              ? {
                  resigned: true,
                }
              : undefined
          }
          tooltip={tableData.id !== user.employeeId ? t('employeeList.table.viewProfile') : undefined}
        />
      ),
    },
    {
      key: 'level',
      title: t('employeeList.table.level'),
      className: 'minWidth-column-l textAlign-left',
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
      sorter: (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto) => {
        const levelA = getSortOrderByEmployeeLevel(employeeA.level, employeeLevels)
        const levelB = getSortOrderByEmployeeLevel(employeeB.level, employeeLevels)
        return levelA - levelB
      },
      render: (tableData: ResignedEmployeeDto) =>
        tableData.level ? t(`employee.contract.level.${tableData.level}`) : <PerText>-</PerText>,
    },
    {
      key: 'salary',
      title: t('employeeList.table.salary'),
      className: 'minWidth-column-m textAlign-right whiteSpace--nowrap',
      sorter: (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto) => compareMoneyValues(employeeA, employeeB, 'salary'),
      render: (tableData: ResignedEmployeeDto) =>
        tableData.salary &&
        tableData.currency && <div style={{ float: 'right' }}>{formatCurrency(tableData.salary, tableData.currency, true)}</div>,
    },
    {
      key: 'targetBonus',
      title: t('employeeList.table.targetBonus'),
      className: 'minWidth-column-m textAlign-right whiteSpace--nowrap',
      sorter: (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto) => compareMoneyValues(employeeA, employeeB, 'targetBonus'),
      render: (tableData: ResignedEmployeeDto) => (
        <PerText color="secondary" fontSize="xs">
          {tableData.bonus && tableData.currency ? formatCurrency(tableData.bonus, tableData.currency, true) : '-'}
        </PerText>
      ),
    },
    {
      key: 'replyEntryDate',
      title: t('employeeList.table.replyEntryDate'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto) => sortDates(employeeA, employeeB, 'startDate'),
      render: (tableData: ResignedEmployeeDto) => (
        <PerText color="secondary" fontSize="xs">
          {formatDateShort(tableData.startDate, true)}
        </PerText>
      ),
    },
    {
      key: 'contractValidTo',
      title: t('employeeList.table.contractValidTo'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto) => sortDates(employeeA, employeeB, 'endDate'),
      render: (tableData: ResignedEmployeeDto) => (
        <PerText color="secondary" fontSize="xs">
          {formatDateShortWithTimezone(tableData.endDate, tableData.timezone)}
        </PerText>
      ),
    },
    {
      key: 'reason',
      title: t('employeeList.resignedEmployeeList.table.reason'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto) =>
        cancellationReasonTranslation(employeeA).localeCompare(cancellationReasonTranslation(employeeB)),
      render: (tableData: ResignedEmployeeDto) => (
        <PerText color="secondary" fontSize="xs">
          {cancellationReasonTranslation(tableData)}
        </PerText>
      ),
    },
    {
      key: 'openBonus',
      title: t('employeeList.resignedEmployeeList.table.openBonus'),
      className: 'minWidth-column-m textAlign-right',
      sorter: (employeeA: ResignedEmployeeDto, employeeB: ResignedEmployeeDto) =>
        employeeA.toBePaid && employeeB.toBePaid && employeeA.toBePaid.amount - employeeB.toBePaid.amount,
      render: (tableData: ResignedEmployeeDto) => (
        <PerText>{tableData.toBePaid ? formatCurrency(tableData.toBePaid.amount, tableData.toBePaid.currency, true) : '-'}</PerText>
      ),
    },
    {
      key: 'actions',
      title: t('employeeList.table.actions'),
      className: 'minWidth-column-s textAlign-left',
      width: tableActionButtonsRowWidth(2),
      render: (tableData: ResignedEmployeeDto) => {
        return (
          <PerTableActionButtons
            variant="pastel"
            buttons={[
              {
                icon: 'chat_fill',
                color: 'primary',
                tooltip: t('employeeList.resignedEmployeeList.table.historyTableTooltip', {
                  employeeName: `${tableData.firstName} ${tableData.lastName}`,
                }),
                onClick: (e: any) => {
                  e.preventDefault() // We should refactor button to not emit native event!
                  setSelectedEmployee(tableData)
                  setIsModalVisible(true)
                },
              },
            ]}
          />
        )
      },
    },
  ]

  return (
    <>
      <Table
        className="light small scroll-compensation viewWithTabsAndInfoBox"
        dataSource={listData.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: <PerEmpty icon="employeesNo_stroke" heading={t('global.employeeCompanyRelation.noResignedEmployees')} />,
        }}
      />
      {!!selectedEmployee && (
        <MeetingHistoryModal
          isOpen={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false)
          }}
          employee={selectedEmployee as unknown as EmployeeDisplayTinyInfo}
          isResigned
        />
      )}
    </>
  )
}
